import { PlaceSummary } from '@/hooks/usePlaces';
import { VerificationStatus } from '@/types/places';
import { cn } from '@/lib/utils';
import { MapPin, Navigation, Star } from 'lucide-react';
import { OpenStatusBadge } from './OpenStatusBadge';
import { BlurImage } from '@/components/ui/BlurImage';

interface PlaceCardProps {
  place: PlaceSummary & { 
    opening_hours?: Record<string, { open: string; close: string } | null> | null;
    is_featured?: boolean;
  };
  onClick: () => void;
  compact?: boolean;
}

export const PlaceCard = ({ place, onClick, compact = false }: PlaceCardProps) => {
  return (
    <div 
      className={cn(
        "flex items-center gap-3 p-3 rounded-xl bg-card cursor-pointer",
        "transition-all duration-300 ease-out border",
        "hover:-translate-y-0.5 hover:shadow-lg active:scale-[0.98]",
        place.is_featured 
          ? "border-amber-400/60 ring-1 ring-amber-400/40 bg-gradient-to-r from-amber-500/10 to-yellow-500/5 shadow-amber-500/10 shadow-sm hover:border-amber-400/80 hover:shadow-amber-500/20" 
          : "border-border/30 hover:border-primary/30 hover:shadow-primary/10"
      )}
      onClick={onClick}
    >
      <div className={cn(
        'flex-shrink-0 rounded-xl overflow-hidden bg-muted relative',
        'transition-transform duration-300',
        compact ? 'w-14 h-14' : 'w-16 h-16'
      )}>
        {place.cover_photo ? (
          <BlurImage 
            src={place.cover_photo} 
            alt={place.name}
            className="w-full h-full"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground bg-gradient-to-br from-primary/10 to-accent/10">
            <MapPin className="w-5 h-5 text-primary/50" />
          </div>
        )}
        {place.is_featured && (
          <div className="absolute top-0.5 right-0.5 p-0.5 rounded-full bg-yellow-500 animate-glow-pulse">
            <Star className="w-2.5 h-2.5 text-white fill-white" />
          </div>
        )}
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <h3 className={cn(
            'font-semibold text-foreground truncate transition-colors duration-200',
            compact ? 'text-sm' : 'text-base'
          )}>
            {place.name}
          </h3>
          {place.is_featured && (
            <span className="flex-shrink-0 px-1.5 py-0.5 text-[10px] font-medium bg-gradient-to-r from-amber-500/20 to-yellow-500/20 text-amber-700 dark:text-amber-400 rounded-full border border-amber-400/30">
              ✨ Tu pourrais aimer
            </span>
          )}
        </div>
        
        <div className="flex items-center gap-1 text-muted-foreground mt-0.5">
          {place.distance_km !== null ? (
            <>
              <Navigation className="w-3 h-3 flex-shrink-0" />
              <span className={cn('truncate', compact ? 'text-xs' : 'text-sm')}>
                {place.distance_km} km
              </span>
            </>
          ) : (
            <>
              <MapPin className="w-3 h-3 flex-shrink-0" />
              <span className={cn('truncate', compact ? 'text-xs' : 'text-sm')}>
                {place.address}
              </span>
            </>
          )}
        </div>
        
        <div className="mt-1.5 flex items-center gap-1.5">
          <OpenStatusBadge openingHours={place.opening_hours} compact={compact} />
          <StatusBadge status="validated" compact={compact} />
        </div>
      </div>
    </div>
  );
};

interface StatusBadgeProps {
  status: VerificationStatus;
  compact?: boolean;
}

export const StatusBadge = ({ status, compact = false }: StatusBadgeProps) => {
  const statusConfig = {
    validated: {
      label: 'Validé',
      className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
    },
    under_review: {
      label: 'En révision',
      className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    },
    pending: {
      label: 'En attente',
      className: 'bg-muted text-muted-foreground',
    },
  };

  const config = statusConfig[status];

  return (
    <span className={cn(
      'inline-flex items-center rounded-full font-medium transition-all duration-200',
      compact ? 'px-2 py-0.5 text-[10px]' : 'px-2.5 py-0.5 text-xs',
      config.className
    )}>
      {config.label}
    </span>
  );
};
